import React, { FC, useCallback, useEffect, useState, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { toast } from 'react-toastify'
import { isNumber } from '@sparling/financial-common'
import { useSelector } from 'react-redux'
import { InputSelect } from '../../../../components/InputSelect'
import { InputField } from '../../../../components/InputField'
import { Modal } from '../../../../components/Modal'
import { resolver } from './resolver'
import { currencyCryptoOrFiat } from '../../../../constants/crmCurrencies'
import { CRM_TRANSACTION_TYPE } from '../../../../constants/CrmTransactionType'
import { useRefresh, useRest } from '../../../../components/Context'
import { Loader } from '../../../../components/Loader'
import { availableCurrenciesSelector } from '../../../../store/selectors'
import { createEnum } from '../../../../utils/createEnum'

const useStyles = makeStyles((theme: Theme) => ({
  modalInput: {
    width: 300,
  },
  modalItem: {
    marginTop: 20,
  },
  subtitle: {
    color: '#b2b3b5',
    fontWeight: 'normal',
    marginBottom: 8,
    fontSize: 14,
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(3),
  },
}))

interface IAddAsset {
  isOpen: boolean
  modalClose: () => void
  userId: string | undefined
}

type FormInputs = {
  asset: string
  type: string
  amount: number
  userId: string | undefined
}

const AddTransaction: FC<IAddAsset> = ({ isOpen, modalClose, userId }) => {
  const formMethods = useForm<FormInputs>({
    mode: 'onBlur',
    resolver,
    shouldFocusError: true,
    shouldUnregister: true,
  })
  const classes = useStyles()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const [loading, setLoading] = useState<boolean>(false)
  const [currencyType, setCurrencyType] = useState<string | undefined>()
  const [isErrorCrypto, setErrorCrypto] = useState<boolean>(false)
  const [isErrorFiat, setErrorFiat] = useState<boolean>(false)
  const allCurrencies = useSelector(availableCurrenciesSelector)

  const { handleSubmit, watch, getValues } = formMethods

  const isErrorAmount = useMemo(() => isErrorCrypto || isErrorFiat, [isErrorCrypto, isErrorFiat])
  const textErrorAmount = useMemo(() => {
    if (isErrorCrypto) {
      return 'max 8 digits after the dot'
    }
    if (isErrorFiat) {
      return 'max 2 digits after the dot'
    }

    return ''
  }, [isErrorCrypto, isErrorFiat])

  const clearErrorAmount = useCallback(async () => {
    setTimeout(() => {
      setCurrencyType('')
      setErrorCrypto(false)
      setErrorFiat(false)
    }, 300)
  }, [])

  const onBlurAmount = (type?: string) => {
    const typeName = type && typeof type === 'string'
    if ((typeName && type) || currencyType) {
      const amount = Number(getValues('amount'))
      if (isNumber(amount)) {
        const amountToString = amount.toString()
        if ((typeName && type === 'crypto') || currencyType === 'crypto') {
          setErrorFiat(false)
          if (/\./.test(amountToString)) {
            setErrorCrypto(!/^[0-9]+\.[0-9]{1,8}$/.test(amountToString))
          } else {
            setErrorCrypto(!/[0-9]*/.test(amountToString))
          }
        } else if ((typeName && type === 'fiat') || currencyType === 'fiat') {
          setErrorCrypto(false)
          if (/\./.test(amountToString)) {
            setErrorFiat(!/^[0-9]+\.[0-9]{1,2}$/.test(amountToString))
          } else {
            setErrorFiat(!/[0-9]*/.test(amountToString))
          }
        }
      }
    }
  }

  useEffect(() => {
    const subscription = watch(async (value, { name }) => {
      if (name === 'asset') {
        setCurrencyType(currencyCryptoOrFiat(value[name]))
        onBlurAmount(currencyCryptoOrFiat(value[name]))
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line
  }, [watch])

  useEffect(() => {
    if (!isOpen) clearErrorAmount()
    setLoading(false)
    // eslint-disable-next-line
  }, [isOpen])

  const onSubmit = useCallback((data: FormInputs) => {
    const postData = data
    postData.userId = userId
    postData.type = postData.type.toLowerCase()
    setLoading(true)

    rxios.post('transaction', postData).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        toast.error(error.response.data.message)
      },
      () => {
        setLoading(false)
        toast.success('Transaction has been added')
        modalClose()
        saveIsRefresh(true)
        formMethods.reset()
      },
    )
    // eslint-disable-next-line
  }, [])

  return (
    <Modal
      title="Add Transaction"
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      submitTitle={'Submit'}
      width={350}
    >
      {loading && <Loader />}
      <FormProvider {...formMethods}>
        <Grid container direction="column">
          <Grid item>
            <Grid container justifyContent="space-between" direction="column">
              <InputSelect
                labelName={'Currency'}
                inputName="asset"
                placeholder="Select"
                width={300}
                enums={createEnum(allCurrencies)}
              />
            </Grid>
          </Grid>
          <Grid item className={classes.modalItem}>
            <Grid container justifyContent="space-between" direction="column">
              <InputSelect
                labelName={'Transaction Type'}
                inputName="type"
                placeholder="Select"
                width={300}
                enums={CRM_TRANSACTION_TYPE}
              />
            </Grid>
          </Grid>
          <Grid item className={classes.modalItem}>
            <Grid container justifyContent="space-between" direction="column">
              <div className={classes.modalInput}>
                <InputField
                  labelName={'Amount'}
                  onBlur={onBlurAmount}
                  isCustomError={isErrorAmount}
                  errorTextCustomError={textErrorAmount}
                  inputName="amount"
                  inputType="text"
                  placeholder="Enter Amount"
                />
              </div>
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoAddTransaction = React.memo(AddTransaction)
